import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import BaseSelect, { components } from 'react-select';
import BaseCreatable from 'react-select/creatable';
import Icon from './Icon';
import Label from './Label';

/**
 * Prop type interfaces
 */
const shapes = {
  OPTION: PropTypes.shape({
    label: PropTypes.node,
    // eslint-disable-next-line react/forbid-prop-types
    value: PropTypes.any,
  }),
};

/**
 * Styles
 */

/**
 * Helper to extend existing Select styles
 *
 * @param {object<object|function>} overrides - Style overrides
 * @return {object} Extended styles
 */
const extend = overrides =>
  Object.entries(overrides).reduce((acc, [key, value]) => {
    acc[key] = (provided, state) => ({
      ...provided,
      ...(typeof value === 'function' ? value(state) : value),
    });
    return acc;
  }, {});

/**
 * Base styles
 */
const base = extend({
  // Icon for clearing all multi selected values
  clearIndicator: {
    alignItems: 'center',
    color: 'var(--dropdown-inactive)',
    display: 'flex',
    height: '16px',
    marginRight: '8px',
    padding: '0px',
    transition: 'color 200ms ease-in-out',
    width: '16px',
    ':hover': {
      color: 'var(--dropdown-active)',
    },
  },

  // Parent container for dropdown
  container: {
    fontSize: 'var(--regular)',
    maxWidth: '400px',
    minWidth: '256px',
  },

  // Inner container for dropdown
  control: ({ isDisabled, isFocused, selectProps: { error } }) => ({
    background: 'var(--dropdown-bg)',
    borderColor: 'var(--dropdown-inactive)',
    borderRadius: '6px',
    boxShadow: 'none',
    cursor: 'pointer',
    minHeight: '40px',
    transition: 'border-color 200ms ease-in-out',
    ':hover': {
      borderColor: 'var(--dropdown-active)',
    },

    ...(isFocused && {
      borderColor: 'var(--dropdown-active)',
      ':hover': {
        borderColor: 'var(--dropdown-active)',
      },
    }),

    ...(isDisabled && {
      background: 'var(--dropdown-disabled)',
      borderColor: 'var(--dropdown-disabled)',
      color: 'var(--dropdown-disabled-text)',
      ':hover': {
        borderColor: 'var(--dropdown-disabled)',
      },
    }),

    ...(error && {
      borderColor: 'var(--danger-dark)',
      ':hover': {
        borderColor: 'var(--danger-dark)',
      },
    }),
  }),

  // Caret icon indicating whether menu is open
  dropdownIndicator: ({ isDisabled, selectProps: { menuIsOpen } }) => ({
    alignItems: 'center',
    color: 'var(--dropdown-inactive)',
    display: 'flex',
    height: '14px',
    marginRight: '12px',
    padding: '0px',
    width: '14px',
    ':hover': {
      color: 'var(--dropdown-inactive)',
    },

    ...(isDisabled && {
      color: 'var(--dropdown-disabled-text)',
    }),

    ...(menuIsOpen && {
      transform: 'rotate(-180deg)',
    }),
  }),

  // Separator between input and icon/indicators
  indicatorSeparator: {
    display: 'none',
  },

  // Dropdown text input
  input: {
    color: 'var(--dropdown-text)',
  },

  // Dropdown menu container
  menu: {
    borderRadius: '6px',
    boxShadow: 'var(--dropdown-menu-shadow)',
    color: 'var(--dropdown-text)',
    marginTop: '6px',
    padding: '8px',
  },

  // Container of menu when rendered through a Portal
  menuPortal: {
    // FIXME: Needs to be greater than modal z-indices...
    zIndex: 99999,
  },

  // Multi value tags
  multiValue: {
    borderRadius: '4px',
    background: 'var(--dropdown-option-bg)',
  },

  // Multi value remove icon
  multiValueRemove: {
    color: 'var(--dropdown-inactive)',
    ':hover': {
      color: 'var(--dropdown-active)',
    },
  },

  // Dropdown menu options
  option: ({ isFocused, isSelected }) => ({
    borderRadius: '6px',
    cursor: 'pointer',

    ...(isFocused && {
      background: 'var(--dropdown-option-bg)',
    }),

    ...(isSelected && {
      background: 'none',
      color: 'var(--dropdown-option-selected)',
      fontWeight: 'var(--bold)',
      ':hover': {
        background: 'var(--dropdown-option-bg)',
      },
    }),
  }),

  // Placeholder text when no value selected
  placeholder: {
    color: 'var(--dropdown-placeholder)',
  },

  // Selected text for single value dropdowns
  singleValue: {
    color: 'var(--dropdown-text)',
  },
});

/**
 * Borderless styles
 */
const borderless = extend({
  // Borderless and background change on hover/value
  control: ({ hasValue, isFocused }) => ({
    background: 'var(--dropdown-bg)',
    border: 'none',
    boxShadow: 'none',
    cursor: 'pointer',
    transition: 'background 200ms ease-in-out',
    ':hover': {
      background: 'var(--dropdown-disabled)',
    },

    ...((isFocused || hasValue) && {
      background: 'var(--dropdown-disabled)',
    }),
  }),

  // Remove the dropdown indicator
  dropdownIndicator: {
    display: 'none',
  },
});

/**
 * Custom subcomponents
 */

/* eslint-disable react/prop-types */

/**
 * Control with icon support
 */
const Prefix = styled(Icon)`
  color: var(--dropdown-icon);
  font-size: var(--regular);
  margin-left: 12px;
`;

export function Control({ children, ...props }) {
  const {
    selectProps: { icon },
  } = props;

  return (
    <components.Control {...props}>
      {icon && <Prefix icon={icon} />}
      {children}
    </components.Control>
  );
}

/**
 * Two-Line Option with label and description.
 */
export const OptionLabel = styled.div`
  color: var(--dropdown-text);
  font-weight: var(--bold);
  font-size: var(--regular);
`;

const Description = styled.div`
  color: var(--dropdown-text);
  font-size: var(--regular);
  font-weight: var(--normal);
`;

export const OptionWrapper = styled.div`
  ${({ isSelected }) =>
    isSelected &&
    `
     color: var(--dropdown-option-selected);
     font-weight: bold;

     ${OptionLabel} {
      color: var(--dropdown-option-selected);
      font-weight: bold;
     }

     ${Description} {
      font-weight: var(--normal);
     }
    `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
     ':hover': {
      borderColor: 'var(--dropdown-disabled)',
     },
     color: var(--dropdown-disabled-text);
     font-weight: bold;

     ${OptionLabel} {
      color: var(--dropdown-disabled-text);
      font-weight: bold;
     }

     ${Description} {
      font-weight: var(--normal);
      color: var(--dropdown-disabled-text);
     }
    `}
`;

/**
 * Option with aria role.
 */
export function Option({
  children,
  data,
  innerProps,
  isSelected,
  isDisabled,
  ...props
}) {
  const child = data.description ? (
    <OptionWrapper isSelected={isSelected} $isDisabled={isDisabled}>
      <OptionLabel>{data.label}</OptionLabel>
      <Description>{data.description}</Description>
    </OptionWrapper>
  ) : (
    children
  );

  return (
    <components.Option
      {...props}
      data={data}
      innerProps={{
        ...innerProps,
        role: 'option',
      }}
      isSelected={isSelected}
      isDisabled={isDisabled}
    >
      {child}
    </components.Option>
  );
}

/**
 * Clear indicator with aria label
 */
export function ClearIndicator({ innerProps, ...props }) {
  return (
    <components.ClearIndicator
      {...props}
      innerProps={{
        ...innerProps,
        'aria-label': 'Clear options',
      }}
    />
  );
}

/**
 * Multi value remove with aria label
 */
export function MultiValueRemove({ innerProps, ...props }) {
  return (
    <components.MultiValueRemove
      {...props}
      innerProps={{
        ...innerProps,
        'aria-label': 'Clear option',
      }}
    />
  );
}

/* eslint-enable react/prop-types */

/**
 * Create select component for base component
 *
 * @param {(BaseSelect|BaseCreatable)} Component - Base component to style
 * @return {React.Component} Styled react-select component
 *
 */
function createSelect(Component) {
  const isCreatable = Component === BaseCreatable;
  const Base = ({
    components: replacements,
    icon,
    inputValue: initialInputValue,
    isMulti,
    kind = 'default',
    onChange,
    onInputChange,
    options: initialOptions,
    portal = false,
    styles,
    value: initialValue,
    ...props
  }) => {
    const [options, setOptions] = useState(initialOptions);
    const [value, setValue] = useState(initialValue);
    const [inputValue, setInputValue] = useState(initialInputValue);

    useEffect(() => {
      setOptions(initialOptions);
    }, [initialOptions]);
    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);
    useEffect(() => {
      setInputValue(initialInputValue);
    }, [initialInputValue]);

    const handleBlur = () => {
      const label = inputValue?.trim() || '';
      const exists = options.find(option => option.label === label);

      if (!label) {
        return;
      }

      // If the typed value exactly matches an existing value, use the one from the options
      const option = exists || { label, value: label };

      // Add value to selected options
      if (isMulti) {
        setValue([...(value || []), option]);
      } else {
        setValue(option);
      }

      // Clear input value
      setInputValue('');

      onChange?.(option);
    };

    const handleInputChange = (textInput, { action }) => {
      if (action === 'input-change') {
        setInputValue(textInput);
      }
      if (action === 'input-blur' && isCreatable) {
        handleBlur();
      }

      onInputChange?.(textInput, { action });
    };

    const handleChange = (selected, ...rest) => {
      setValue(selected);
      setInputValue('');
      onChange?.(selected, ...rest);
    };

    return (
      <Component
        components={{
          ClearIndicator,
          Control,
          MultiValueRemove,
          Option,
          ...replacements,
        }}
        // Convenience prop that allows an icon to be specified to be rendered at
        // the start of the input
        icon={icon}
        // Render the select dropdown menu in a Portal to ensure rendering is not
        // hindered by things like parent overflow styles. By default the menu
        // will NOT render through the portal due to some open issues regarding
        // positioning with scroll, but useful when rendered within things like
        // modals.
        menuPortalTarget={portal && document.querySelector('#portal-root')}
        // Use fixed positioning when rendering within a context that will not
        // scroll such as the content of a modal via a portal. This ensures that
        // scrolls from the parent elements do not offset the menu accidentally.
        // Lifewise, use absolute positioning when the select context may be
        // scrollable to ensure scroll offsets appropriately reposition the menu
        // as well.
        menuPosition={portal ? 'fixed' : 'absolute'}
        // Apply the custom styles along with any additional overrides if
        // absolutely required
        styles={{
          ...base,
          ...(kind === 'borderless' ? borderless : {}),
          ...styles,
        }}
        // Managed props
        inputValue={inputValue}
        isMulti={isMulti}
        onChange={handleChange}
        onInputChange={handleInputChange}
        options={options}
        value={value}
        // Since this component is just a styled wrapper around `react-select`, we
        // want to forward any props we do not need to intercept. This ensures the
        // full functionality of the library is kept intact.
        {...props}
      />
    );
  };

  // Implicitly inherits `react-select` props
  Base.propTypes = {
    // Component overrides
    components: PropTypes.objectOf(PropTypes.elementType),
    // Whether or not the select is on error state
    error: PropTypes.bool,
    // Optional leading icon
    icon: PropTypes.string,
    // UI variants
    kind: PropTypes.oneOf(['default', 'borderless']),
    // Convenience prop to render menu within the portal root
    portal: PropTypes.bool,
    // Additional style overrides
    styles: PropTypes.objectOf(PropTypes.func),

    // Managed props from `react-select`
    inputValue: PropTypes.string,
    isMulti: PropTypes.bool,
    onChange: PropTypes.func,
    onInputChange: PropTypes.func,
    options: PropTypes.arrayOf(shapes.OPTION),
    value: PropTypes.oneOfType([
      PropTypes.arrayOf(shapes.OPTION),
      shapes.OPTION,
    ]),
  };

  return styled(Base)``;
}

export const Select = createSelect(BaseSelect);
export const Creatable = createSelect(BaseCreatable);

export const SelectLabel = styled(Label)`
  > ${Select}, > ${Creatable} {
    margin-top: 6px;
  }

  & + && {
    margin-top: 16px;
  }
`;
